<template>
  <div class="ps-change-list">
    <div v-if="note" class="ps-change-list__note">{{ note }}</div>
    <a-spin :spinning="loading">
      <!-- ===== 变更历史：时间轴（对齐原型） ===== -->
      <template v-if="mode === 'history'">
        <div v-if="!historyTimeline.length" class="ps-change-list__empty">
          <div class="big">{{ emptyIcon }}</div>
          {{ emptyText }}
        </div>
        <div v-else class="ps-timeline">
          <div
            v-for="entry in historyTimeline"
            :key="entry.key"
            class="tl-item"
          >
            <div class="tl-dot" :class="entry.color"></div>
            <div class="tl-card" :class="entry.color">
              <div class="tl-top">
                <span class="tl-ver" :class="entry.color">
                  {{ entry.verText }}　{{ entry.stateText }}　{{ entry.title }}
                </span>
                <span class="tl-time">{{ entry.time }}</span>
              </div>
              <p v-if="entry.msg" class="tl-msg">{{ entry.msg }}</p>
              <div v-if="entry.tags.length" class="tl-tags">
                <span
                  v-for="t in entry.tags"
                  :key="t"
                  class="tl-tag"
                  >{{ t }}</span
                >
              </div>
              <div v-if="entry.versionId" class="tl-actions">
                <button
                  type="button"
                  class="ps-act-btn ps-act-btn--detail"
                  @click="openVersionDetail(entry)"
                >
                  查看详情
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- ===== 变更请求：按本次提交整批 ===== -->
      <template v-else>
        <div v-if="!requestBatches.length" class="ps-change-list__empty">
          <div class="big">{{ emptyIcon }}</div>
          {{ emptyText }}
        </div>
        <div v-else class="ps-change-list__groups">
          <div
            v-for="batch in requestBatches"
            :key="batch.key"
            class="ps-batch"
          >
            <div v-if="batch.showHead" class="ps-batch__head">
              <div class="ps-batch__meta">
                <span class="ps-batch__title">本次提交</span>
                <span v-if="batch.submitTime">{{ batch.submitTime }}</span>
                <span>{{ batch.itemCount }} 条变更</span>
              </div>
              <div v-if="batch.canAudit" class="ps-batch__actions">
                <button
                  type="button"
                  class="ps-act-btn ps-act-btn--pass"
                  @click="handleBatchAudit(batch, 'pass')"
                >
                  整批通过
                </button>
                <button
                  type="button"
                  class="ps-act-btn ps-act-btn--reject"
                  @click="handleBatchAudit(batch, 'reject')"
                >
                  整批驳回
                </button>
              </div>
            </div>
            <div
              v-for="group in batch.moduleGroups"
              :key="batch.key + '-' + group.taskType"
              class="ps-change-group"
            >
              <h3 class="ps-change-group__title">
                {{ group.title }}
                <span class="ps-change-group__count"
                  >{{ group.items.length }} 条</span
                >
              </h3>
              <div
                v-for="item in group.items"
                :key="item.detailId || item.id"
                class="ps-req-card"
              >
                <div class="ps-req-card__top">
                  <div class="ps-req-card__title">
                    <StatusBadge
                      :status="item.auditState"
                      :text="statusText(item.auditState)"
                    />
                    <span class="ps-req-card__op">{{
                      typeMap[item.operation] || item.operation || "-"
                    }}</span>
                    <span v-if="item.description" class="ps-req-card__desc">{{
                      item.description
                    }}</span>
                  </div>
                  <div class="ps-req-card__time">
                    {{ item.lastSubmitTime || item.submitTime || "-" }}
                  </div>
                </div>
                <div v-if="item.remark" class="ps-req-card__remark">
                  驳回原因：{{ item.remark }}
                </div>

                <!-- 卡片内摘要：与查看对比同一套展示字段 -->
                <div
                  v-if="
                    item.operation === 'UPDATE' &&
                      summaryFields(item).length
                  "
                  class="ps-req-fields"
                >
                  <div
                    v-for="(f, idx) in summaryFields(item)"
                    :key="idx"
                    class="ps-req-field"
                  >
                    <span class="fname">{{ f.name }}</span>
                    <span
                      v-if="isComparePreviewable(f, 'old')"
                      class="old ps-cmp-link"
                      @click.stop="onComparePreview(f, 'old')"
                      >{{ f.old }}</span
                    >
                    <span v-else class="old">{{ f.old }}</span>
                    <span class="arrow">→</span>
                    <span
                      v-if="isComparePreviewable(f, 'new')"
                      class="new ps-cmp-link"
                      @click.stop="onComparePreview(f, 'new')"
                      >{{ f.new }}</span
                    >
                    <span v-else class="new">{{ f.new }}</span>
                  </div>
                </div>
                <div
                  v-else-if="
                    item.operation === 'INSERT' ||
                      item.operation === 'DELETE'
                  "
                  class="ps-req-card__hint"
                >
                  {{
                    item.operation === "INSERT"
                      ? "新增一条记录，点击「查看对比」查看详情"
                      : "删除一条记录，点击「查看对比」查看详情"
                  }}
                </div>

                <div class="ps-req-card__actions">
                  <button
                    type="button"
                    class="ps-act-btn ps-act-btn--compare"
                    @click="openCompare(item, group.title)"
                  >
                    ⇄ 查看对比
                  </button>
                  <button
                    v-if="
                      showCancelAction &&
                        mode === 'requests' &&
                        String(item.auditState) === '1'
                    "
                    type="button"
                    class="ps-act-btn ps-act-btn--cancel"
                    @click="handleCancel(item, group.title)"
                  >
                    撤销申请
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </a-spin>

    <!-- 版本对比弹窗（左右分栏，列出整条信息） -->
    <div
      class="ps-modal-mask"
      :class="{ open: compareVisible }"
      @click.self="closeCompare"
    >
      <div class="ps-modal" v-if="compareItem">
        <div class="ps-modal-head">
          <div>
            <h3>版本对比</h3>
            <p>
              左：变更前 右：变更后
              <span class="c-red">红＝删除</span>
              <span class="c-green">绿＝新增/修改</span>
            </p>
          </div>
          <button type="button" class="ps-close-btn" @click="closeCompare">
            ✕
          </button>
        </div>
        <div class="ps-modal-body">
          <div class="ps-cmp-meta">
            <span>{{ compareModuleTitle }}</span>
            <span class="dot">·</span>
            <span>{{
              typeMap[compareItem.operation] || compareItem.operation
            }}</span>
            <span class="dot">·</span>
            <span>{{ statusText(compareItem.auditState) }}</span>
            <span class="dot">·</span>
            <span>{{
              compareItem.lastSubmitTime || compareItem.submitTime || "-"
            }}</span>
          </div>

          <div v-if="compareFieldRows.length" class="ps-compare-grid">
            <div>
              <div class="ps-cmp-head gray">变更前</div>
              <div class="ps-cmp-body">
                <div
                  v-for="(f, i) in compareFieldRows"
                  :key="'l' + i"
                  class="ps-cmp-line"
                >
                  <span class="k">{{ f.name }}：</span>
                  <span
                    v-if="isComparePreviewable(f, 'old')"
                    class="ps-cmp-link"
                    @click.stop="onComparePreview(f, 'old')"
                    >{{ f.old }}</span
                  >
                  <span v-else>{{ f.old }}</span>
                </div>
              </div>
            </div>
            <div>
              <div class="ps-cmp-head blue">变更后</div>
              <div class="ps-cmp-body blue-border">
                <template v-if="compareItem.operation === 'DELETE'">
                  <div class="ps-cmp-deleted">已删除这条信息</div>
                </template>
                <template v-else>
                  <div
                    v-for="(f, i) in compareFieldRows"
                    :key="'r' + i"
                    class="ps-cmp-line"
                  >
                    <span class="k">{{ f.name }}：</span>
                    <span
                      v-if="isComparePreviewable(f, 'new')"
                      class="ps-cmp-link"
                      :class="{ 'diff-new': f.changed }"
                      @click.stop="onComparePreview(f, 'new')"
                      >{{ f.new }}</span
                    >
                    <span v-else-if="f.changed" class="diff-new">{{
                      f.new
                    }}</span>
                    <span v-else>{{ f.new }}</span>
                  </div>
                </template>
              </div>
            </div>
          </div>
          <div v-else class="ps-cmp-empty">暂无可对比的详情数据</div>

          <div
            v-if="
              compareItem.operation === 'UPDATE' &&
                compareFieldRows.filter((f) => f.changed).length
            "
            class="ps-change-alert"
          >
            ⚠ 检测到
            <b>{{ compareFieldRows.filter((f) => f.changed).length }}</b>
            处变更：{{
              compareFieldRows
                .filter((f) => f.changed)
                .map((f) => f.name)
                .join("、")
            }}。
          </div>
        </div>
        <div class="ps-modal-foot">
          <button
            type="button"
            class="ps-act-btn ps-act-btn--cancel"
            @click="closeCompare"
          >
            关闭
          </button>
        </div>
      </div>
    </div>

    <div class="preview-pop-box" v-if="showPdf">
      <div class="preview-boxs" style="height: 90%">
        <newpreview
          :previewUrl="previewUrl"
          :dialogTitle="previewTitle"
          @closePreview="showPdf = false"
        />
      </div>
    </div>
    <div class="preview-pop-box" v-if="showVideo">
      <div class="preview-boxs" style="height: 600px">
        <videopreview :sj="videoData" @closePreview="showVideo = false" />
      </div>
    </div>

    <!-- 版本详情弹窗：展示该卡片对应版本快照（与版本页同口径） -->
    <div
      class="ps-modal-mask"
      :class="{ open: detailVisible }"
      @click.self="closeDetail"
    >
      <div class="ps-modal ps-modal--detail" v-if="detailItem">
        <div class="ps-modal-head">
          <div>
            <h3>版本详情</h3>
            <p>{{ detailTitle }}</p>
          </div>
          <button type="button" class="ps-close-btn" @click="closeDetail">
            ✕
          </button>
        </div>
        <div class="ps-modal-body">
          <div v-if="detailLegacy" class="ps-version__legacy">
            旧快照仅含基本信息，模块明细不可用。
          </div>
          <CurrentStatusPanel
            v-if="applyId"
            :apply-id="applyId"
            :apply-type="applyType"
            :snapshot-state="detailSnapshotState"
            :snapshot-mode="true"
          />
        </div>
        <div class="ps-modal-foot">
          <button
            type="button"
            class="ps-act-btn ps-act-btn--cancel"
            @click="closeDetail"
          >
            关闭
          </button>
        </div>
      </div>
    </div>

    <RejectReason ref="rejectReason" @handleRejectSuccess="onRejectSuccess" />
    <a-modal
      title="整批驳回"
      :visible="rejectVisible"
      ok-text="确认驳回"
      cancel-text="取消"
      destroy-on-close
      @ok="confirmBatchReject"
      @cancel="rejectVisible = false"
    >
      <a-textarea
        v-model="rejectRemark"
        placeholder="请输入驳回原因"
        :rows="4"
      />
    </a-modal>
  </div>
</template>

<script>
import StatusBadge from "./StatusBadge.vue";
import RejectReason from "@/components/auditPage/rejectReason.vue";
import videopreview from "@/components/common/videopreview";
import newpreview from "@/components/common/newpreviews";
import CurrentStatusPanel from "./CurrentStatusPanel.vue";
import { postAction } from "@/api";
import {
  getModuleAuditList,
  getModulePendingAuditList,
  auditByBatchNumber,
  canRoleAuditAtLevel,
  getAuditLevel,
  getVersionHistory,
  getVersionDetail,
} from "@/api/auditTask.js";
import {
  AUDIT_TYPE_MAP,
  FIELD_LABELS,
  TYPE_MAP,
  FILE_VIEW_MAP,
  CURSOR_POINTER,
} from "@/components/auditPage/data.js";

export default {
  name: "ModuleChangeList",
  components: {
    StatusBadge,
    RejectReason,
    videopreview,
    newpreview,
    CurrentStatusPanel,
  },
  props: {
    applyId: {
      type: String,
      required: true,
    },
    applyType: {
      type: [String, Number],
      default: "1",
    },
    mode: {
      type: String,
      default: "requests",
    },
  },
  data() {
    return {
      loading: false,
      rawMap: {},
      typeMap: TYPE_MAP,
      compareVisible: false,
      compareItem: null,
      compareModuleTitle: "",
      showPdf: false,
      showVideo: false,
      previewUrl: "",
      previewTitle: "文件预览",
      videoData: {},
      rejectVisible: false,
      rejectRemark: "",
      rejectBatch: null,
      /** 版本详情弹窗 */
      detailVisible: false,
      detailItem: null,
      detailTitle: "",
      detailSnapshotState: null,
      detailLegacy: false,
      /** version -> versionId 映射（历史卡片「查看详情」用） */
      versionIdMap: {},
    };
  },
  computed: {
    role() {
      return this.$store.state.role;
    },
    showCancelAction() {
      return this.role === "teacher-role" || this.role === "school-role";
    },
    isAuditorRole() {
      return (
        this.role === "audit-role" ||
        this.role === "education-role" ||
        this.role === "education-guide-role"
      );
    },
    note() {
      if (this.mode === "requests") {
        return this.isAuditorRole
          ? "变更请求按本次提交整批审核。学校/教育局一次通过或驳回本批全部变更；教育局通过后才会写入项目并生成新版本。"
          : "待提交草稿需点「提交变更审核」后才会进入学校/教育局审核。已提交的按批次整批通过或驳回。";
      }
      return "变更历史 = 已通过与已驳回的更新记录。";
    },
    emptyIcon() {
      return this.mode === "requests" ? "✅" : "🕘";
    },
    emptyText() {
      return this.mode === "requests"
        ? "当前没有待处理的变更请求"
        : "暂无变更历史记录";
    },
    moduleTitleMap() {
      const type = String(this.applyType);
      const list = AUDIT_TYPE_MAP[type] || [];
      const map = {};
      list.forEach((m) => {
        map[m.id] = m.title;
      });
      return map;
    },
    /** 变更历史时间轴：按提交批次(版本)分组，每张卡片显示版本号/审核结果/变更标题/审核意见/模块标签 */
    historyTimeline() {
      const items = [];
      Object.entries(this.rawMap || {}).forEach(([taskType, list]) => {
        (list || []).forEach((item) => items.push({ ...item, taskType }));
      });
      const map = {};
      const order = [];
      items.forEach((item) => {
        const isInitial = item.taskType === "INITIAL";
        const key = isInitial
          ? "INITIAL-" + (item.version != null ? item.version : 0)
          : item.batchNumber ||
            (item.projectVersion != null
              ? "pv-" + item.projectVersion
              : "none-" + (item.id || item.detailId || "x"));
        if (!map[key]) {
          map[key] = [];
          order.push(key);
        }
        map[key].push(item);
      });
      const batchVersion = (arr) => {
        const v = arr.map((i) => i.projectVersion).find((x) => x != null);
        return v != null ? v : -1;
      };
      const batchTime = (arr) => {
        let t = "";
        (arr || []).forEach((i) => {
          // 已审核的批次优先显示审核时间(audit_time，与列表卡片驳回时间同源)，
          // 未审核的批次回落到提交时间
          const s = i.auditTime || i.lastSubmitTime || i.submitTime || "";
          if (s > t) t = s;
        });
        return t;
      };
      // 卡片按本批最新时间倒序；时间相同时按版本号倒序
      order.sort((a, b) => {
        const ta = batchTime(map[a]);
        const tb = batchTime(map[b]);
        if (ta !== tb) return tb.localeCompare(ta);
        const va = batchVersion(map[a]);
        const vb = batchVersion(map[b]);
        return vb - va;
      });
      return order.map((key) => {
        const arr = map[key];
        const isInitial = arr.every((i) => i.taskType === "INITIAL");
        const typeOrder = [];
        arr.forEach((item) => {
          const t = item.taskType || "_none_";
          if (typeOrder.indexOf(t) < 0) typeOrder.push(t);
        });
        const sample = arr[0] || {};
        const pv = arr.map((i) => i.projectVersion).find((x) => x != null);
        const stateMeta = this.tlStateMeta(sample.auditState);
        // 标题：首次申报固定；变更批取本批去重后的描述摘要
        let title = "首次申报";
        if (!isInitial) {
          const descs = [];
          arr.forEach((i) => {
            const d = this.cleanDesc(i.description);
            if (d && descs.indexOf(d) < 0) descs.push(d);
          });
          title = descs.length
            ? descs.join("、")
            : typeOrder
                .filter((t) => t !== "_none_")
                .map((t) => this.moduleTitleMap[t] || t)
                .join("、");
        }
        let msg = "";
        // 整批驳回只生成一条审核记录(挂在本批某个任务上)，需从整批里查找非空审核意见
        const batchRemark = arr
          .map((i) => i.remark)
          .find((r) => r && String(r).trim());
        if (isInitial) {
          msg = sample.description || "首次申报审核通过。";
        } else if (batchRemark) {
          msg = "审核意见：" + batchRemark;
        }
        // 已驳回的轮次不显示版本号和查看详情（驳回未生成版本快照）
        const isRejected = !isInitial && String(sample.auditState) === "3";
        return {
          key,
          isInitial,
          color: stateMeta.color,
          verText: isRejected ? "" : pv != null ? `v${pv}.0` : "未生成版本",
          stateText: stateMeta.text,
          title,
          time: batchTime(arr) || "",
          msg,
          tags: this.buildModuleTags(arr, typeOrder, isInitial),
          /** 对应已通过版本的 versionId（有版本才可查看详情；已驳回不显示） */
          versionId:
            !isRejected && pv != null ? this.versionIdMap[pv] || "" : "",
        };
      });
    },
    requestBatches() {
      const items = [];
      Object.entries(this.rawMap || {}).forEach(([taskType, list]) => {
        (list || []).forEach((item) => {
          items.push({ ...item, taskType });
        });
      });
      const map = {};
      const order = [];
      items.forEach((item) => {
        const isDraft = String(item.auditState) === "0";
        const key = isDraft ? "_draft_" : item.batchNumber || "_none_";
        if (!map[key]) {
          map[key] = [];
          order.push(key);
        }
        map[key].push(item);
      });
      return order.map((key) => {
        const batchItems = map[key];
        const byType = {};
        batchItems.forEach((item) => {
          if (!byType[item.taskType]) byType[item.taskType] = [];
          byType[item.taskType].push(item);
        });
        const pending = batchItems.filter((i) => String(i.auditState) === "1");
        const sample = pending[0] || batchItems[0] || {};
        const isDraft = key === "_draft_";
        return {
          key,
          title: isDraft ? "待提交草稿" : "本次提交",
          batchNumber: key === "_none_" || isDraft ? "" : key,
          showHead: true,
          submitTime: sample.lastSubmitTime || sample.submitTime || "",
          itemCount: batchItems.length,
          pendingIds: pending.map((i) => i.id).filter(Boolean),
          canAudit:
            !isDraft &&
            pending.length > 0 &&
            canRoleAuditAtLevel(
              this.role,
              sample.auditState,
              sample.auditLevel
            ),
          moduleGroups: Object.entries(byType).map(([taskType, list]) => ({
            taskType,
            title: this.moduleTitleMap[taskType] || taskType,
            items: list,
          })),
        };
      });
    },
    /** 对比弹窗：整条信息字段（左变更前 / 右变更后） */
    compareFieldRows() {
      return this.buildCompareRows(this.compareItem);
    },
  },
  watch: {
    applyId: {
      immediate: true,
      handler(val) {
        if (val) this.load();
      },
    },
    mode() {
      if (this.applyId) this.load();
    },
  },
  methods: {
    statusText(state) {
      const map = {
        "0": "待提交",
        "1": "待审核",
        "2": "已通过",
        "3": "已驳回",
        "4": "已撤销",
      };
      return map[String(state)] || String(state);
    },
    /** 时间轴状态色与文案（对齐原型：绿=通过 红=驳回 橙=待审 蓝=其他） */
    tlStateMeta(state) {
      const s = String(state);
      if (s === "2") return { color: "green", text: "✔ 已通过" };
      if (s === "3") return { color: "red", text: "✖ 已驳回" };
      if (s === "1") return { color: "orange", text: "🕘 待审核" };
      if (s === "4") return { color: "blue", text: "已撤销" };
      return { color: "blue", text: this.statusText(s) };
    },
    /** 描述摘要：去掉「工作室/品牌/微课」前缀与末尾叹号，得到简洁标题 */
    cleanDesc(d) {
      let s = String(d || "").trim();
      s = s.replace(/^(工作室|品牌|微课)/, "");
      s = s.replace(/！$/, "");
      return s;
    },
    /** 模块标签：模块名 + 操作（新增/修改/删除），如「团队成员·修改」 */
    buildModuleTags(arr, typeOrder, isInitial) {
      if (isInitial) return [];
      const tags = [];
      typeOrder
        .filter((t) => t !== "_none_")
        .forEach((t) => {
          const title = this.moduleTitleMap[t] || t;
          const ops = [];
          arr.forEach((i) => {
            if ((i.taskType || "_none_") === t) {
              const o = this.typeMap[i.operation];
              if (o && ops.indexOf(o) < 0) ops.push(o);
            }
          });
          tags.push(ops.length ? `${title}·${ops.join("、")}` : title);
        });
      return tags;
    },
    normalizeEmpty(val) {
      if (val === null || val === undefined || val === "") return "";
      return String(val);
    },
    isVideoTask(taskType) {
      const t =
        taskType ||
        (this.compareItem && this.compareItem.taskType) ||
        "";
      return t === "WKSP";
    },
    /** 材料类型等枚举转可读文案；按当前 applyType / 模块字段，不用微课规则套工作室 */
    formatCompareValue(key, val, taskType) {
      if (val === null || val === undefined || val === "") return "";
      if (Array.isArray(val)) {
        if (String(this.applyType) === "2" && key === "brandLabels") {
          return val
            .map((path) => {
              const ids = Array.isArray(path) ? path : [path];
              return ids.map((id) => String(id)).join("/");
            })
            .sort()
            .join("、");
        }
        return val.filter(Boolean).join("、");
      }
      if (key === "type" && this.isVideoTask(taskType)) {
        if (String(val) === "0") return "本地视频";
        if (String(val) === "1") return "超链接";
      }
      const s = String(val).trim();
      if (String(this.applyType) === "2") {
        if (key === "brandClassify") {
          if (s === "0" || s === "校园文化品牌") return "校园文化品牌";
          if (s === "1" || s === "专业文化品牌") return "专业文化品牌";
          return s;
        }
        if (key === "brandConnotation") {
          return s
            .split(/[,、]/)
            .map((x) => x.trim())
            .filter(Boolean)
            .sort()
            .join("、");
        }
        if (key === "brandLabels") {
          try {
            const arr = typeof val === "string" ? JSON.parse(val) : val;
            if (Array.isArray(arr)) {
              return arr
                .map((path) => {
                  const ids = Array.isArray(path) ? path : [path];
                  return ids.map((id) => String(id)).join("/");
                })
                .sort()
                .join("、");
            }
          } catch (e) {
            /* 原样展示 */
          }
        }
        if (
          key === "brandExposureRate" ||
          key === "goodRate" ||
          key === "teacherParticipationRate" ||
          key === "studentParticipationRate"
        ) {
          const n = Number(s.replace("%", ""));
          return isNaN(n) ? "" : String(n);
        }
      }
      if (
        key === "foundTime" ||
        key === "birthday" ||
        key === "lxsj" ||
        key === "jxsj" ||
        key === "bsTime" ||
        key === "holdingTime" ||
        key === "cooperativeTime"
      ) {
        if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
      }
      if (
        key === "applyYear" ||
        key === "vintages" ||
        key === "year" ||
        key === "acquireYear" ||
        key === "costYear" ||
        key === "activityYear" ||
        key === "joinYear"
      ) {
        if (/^\d{4}/.test(s)) return s.slice(0, 4);
      }
      if (key === "studioStatus") {
        if (s === "开" || s === "1" || s === "true" || val === true) return "开";
        if (s === "关" || s === "0" || s === "false" || val === false) return "关";
        return s;
      }
      if (
        key === "izResponse" ||
        key === "izDedicated" ||
        key === "izPolitical" ||
        key === "izBsxw"
      ) {
        if (s === "1" || s === "true" || val === true || s === "是") return "是";
        if (s === "0" || s === "false" || val === false || s === "否") return "否";
      }
      if (key === "sex") {
        if (s === "1" || s === "男") return "男";
        if (s === "2" || s === "女") return "女";
      }
      if (key === "tsSzgzsKnow" || key === "tsSzgzsSatisfied") {
        return /%$/.test(s) ? s : `${s}%`;
      }
      return s;
    },
    pickCompareValue(obj, key) {
      if (!obj) return "";
      const keys = [key];
      if (key === "education") keys.push("degree");
      if (key === "degree") keys.push("education");
      if (key === "fileName") keys.push("name");
      if (
        String(this.applyType) === "2" &&
        (key === "basicMajorCategory" ||
          key === "basicMajorSubclass" ||
          key === "basicMajor")
      ) {
        const dict = this.normalizeEmpty(obj[key + "_dictText"]);
        if (dict !== "") return dict;
      }
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        let v = this.normalizeEmpty(obj[k]);
        if (v === "") v = this.normalizeEmpty(obj[k + "_dictText"]);
        if (v !== "") return v;
      }
      return "";
    },
    mergeCompareJson(transStr, rawStr) {
      const raw = this.parseJson(rawStr);
      const trans = this.parseJson(transStr);
      const out = { ...raw };
      Object.keys(trans || {}).forEach((k) => {
        const v = trans[k];
        if (v !== null && v !== undefined && v !== "") {
          out[k] = v;
        }
      });
      return out;
    },
    /** 相同中文名只保留一行（优先有内容的；有变更的优先） */
    dedupeCompareRows(rows) {
      const map = {};
      const order = [];
      rows.forEach((row) => {
        const k = row.name;
        if (!map[k]) {
          map[k] = { ...row };
          order.push(k);
          return;
        }
        const prev = map[k];
        if (!prev.key && row.key) prev.key = row.key;
        if (!prev.beforeData && row.beforeData) prev.beforeData = row.beforeData;
        if (!prev.afterData && row.afterData) prev.afterData = row.afterData;
        if (!prev.taskType && row.taskType) prev.taskType = row.taskType;
        if ((prev.old === "-" || prev.old === "") && row.old !== "-" && row.old !== "") {
          prev.old = row.old;
        }
        if ((prev.new === "-" || prev.new === "") && row.new !== "-" && row.new !== "") {
          prev.new = row.new;
        }
        if (row.changed) prev.changed = true;
        else {
          prev.changed =
            String(prev.old) !== String(prev.new) &&
            !(prev.old === "-" && prev.new === "-");
        }
      });
      return order.map((k) => map[k]);
    },
    parseJson(str) {
      if (!str) return {};
      if (typeof str === "object") return str;
      try {
        return JSON.parse(str);
      } catch (e) {
        return {};
      }
    },
    fieldLabelsOf(taskType) {
      const type = String(this.applyType);
      let typeKey = String(taskType || "");
      if (typeKey === "base") {
        typeKey =
          type === "2"
            ? "PPJIBENXINXI"
            : type === "3"
            ? "WKJIBENXINXI"
            : "GZSJIBENXINXI";
      }
      const raw = (FIELD_LABELS[type] && FIELD_LABELS[type][typeKey]) || {};
      if (type === "3" && typeKey === "WKSP") {
        return {
          type: "视频类型",
          fileName: "思政微课视频",
          filePath: "超链接地址",
          ideologicalElement: "思政元素",
        };
      }
      const skip = {
        id: 1,
        aliId: 1,
        applyId: 1,
        createBy: 1,
        createTime: 1,
        updateBy: 1,
        updateTime: 1,
        sysOrgCode: 1,
        delFlag: 1,
        tenantId: 1,
      };
      const out = {};
      Object.keys(raw).forEach((key) => {
        if (skip[key] || !raw[key]) return;
        out[key] = raw[key];
      });
      return out;
    },
    buildCompareRows(item) {
      if (!item) return [];
      const labels = this.fieldLabelsOf(item.taskType);
      const before = this.mergeCompareJson(item.dataTransBefore, item.dataBefore);
      const after = this.mergeCompareJson(
        item.datTransAfter,
        item.dataAfter || item.applicationData
      );
      const op = item.operation;
      const rows = [];
      Object.keys(labels).forEach((key) => {
        const name = labels[key];
        if (!name) return;
        const oldRaw = this.formatCompareValue(
          key,
          this.pickCompareValue(before, key),
          item.taskType
        );
        const newRaw = this.formatCompareValue(
          key,
          this.pickCompareValue(after, key),
          item.taskType
        );
        if (op === "INSERT") {
          rows.push({
            key,
            name,
            taskType: item.taskType,
            beforeData: before,
            afterData: after,
            old: "-",
            new: newRaw === "" ? "-" : String(newRaw),
            changed: newRaw !== "",
          });
        } else if (op === "DELETE") {
          rows.push({
            key,
            name,
            taskType: item.taskType,
            beforeData: before,
            afterData: after,
            old: oldRaw === "" ? "-" : String(oldRaw),
            new: "-",
            changed: false,
          });
        } else {
          let changed = oldRaw !== newRaw;
          if (
            changed &&
            String(this.applyType) === "2" &&
            (key === "basicMajorCategory" ||
              key === "basicMajorSubclass" ||
              key === "basicMajor")
          ) {
            const oldId = this.normalizeEmpty(before[key]);
            const newId = this.normalizeEmpty(after[key]);
            if (oldId !== "" && newId !== "" && oldId === newId) {
              changed = false;
            } else {
              const oldDict = this.normalizeEmpty(before[key + "_dictText"]);
              const newDict = this.normalizeEmpty(after[key + "_dictText"]);
              if (
                (oldDict !== "" && oldDict === newRaw) ||
                (newDict !== "" && newDict === oldRaw) ||
                (oldDict !== "" && newDict !== "" && oldDict === newDict)
              ) {
                changed = false;
              }
            }
          }
          rows.push({
            key,
            name,
            taskType: item.taskType,
            beforeData: before,
            afterData: after,
            old: oldRaw === "" ? "-" : String(oldRaw),
            new: newRaw === "" ? "-" : String(newRaw),
            changed,
          });
        }
      });
      return this.dedupeCompareRows(rows);
    },
    /** 卡片摘要：只展示有变化的业务字段（保留预览所需数据） */
    summaryFields(item) {
      if (!item || item.operation !== "UPDATE") return [];
      return this.buildCompareRows(item)
        .filter((f) => f.changed)
        .map((f) => ({
          ...f,
          old: f.old === "-" ? "—" : f.old,
          new: f.new === "-" ? "—" : f.new,
        }));
    },
    getFileViewConf(taskType, key) {
      const typeMap = FILE_VIEW_MAP[String(this.applyType)] || {};
      const moduleMap = typeMap[taskType] || {};
      return moduleMap[key] || null;
    },
    looksLikeFileOrUrl(text) {
      if (!text || text === "-") return false;
      const s = String(text);
      if (/^https?:\/\//i.test(s)) return true;
      return /\.(mp4|pdf|doc|docx|xls|xlsx|ppt|pptx|png|jpg|jpeg|gif|zip|rar)(\?|$)/i.test(
        s
      );
    },
    isVideoCompareKey(row) {
      const taskType = row.taskType || "";
      const key = row.key || "";
      if (taskType === "WKSP") {
        return (
          key === "fileName" ||
          key === "name" ||
          key === "filePath" ||
          /视频|超链接/.test(row.name || "")
        );
      }
      return /视频/.test(row.name || "");
    },
    isComparePreviewable(row, side) {
      const text = side === "old" ? row.old : row.new;
      if (!text || text === "-") return false;
      const data = (side === "old" ? row.beforeData : row.afterData) || {};
      const key = row.key || "";
      if (this.isVideoCompareKey(row)) {
        if (String(data.type) === "1") return !!data.filePath;
        return !!(data.aliId || data.filePath || this.looksLikeFileOrUrl(text));
      }
      if (key === "filePath" && this.isVideoTask(row.taskType)) {
        return !!(data.filePath || this.looksLikeFileOrUrl(text));
      }
      const conf = this.getFileViewConf(row.taskType, key);
      if (conf && data[conf.urlName]) return true;
      if (CURSOR_POINTER.indexOf(row.name) >= 0) {
        return !!(
          data.filePath ||
          data.declarationPath ||
          data.activityPlanPath ||
          data.letterOfCommitmentPath ||
          data.aliId ||
          this.looksLikeFileOrUrl(text)
        );
      }
      return this.looksLikeFileOrUrl(text);
    },
    onComparePreview(row, side) {
      const text = side === "old" ? row.old : row.new;
      const data = Object.assign(
        {},
        side === "old" ? row.beforeData : row.afterData
      );
      const key = row.key || "";
      if (
        this.isVideoCompareKey(row) &&
        (key === "filePath" || String(data.type) === "1")
      ) {
        const url = data.filePath || text;
        if (url && url !== "-") {
          this.openLink(url);
          return;
        }
      }
      if (
        this.isVideoCompareKey(row) ||
        /\.mp4/i.test(String(text)) ||
        (this.isVideoTask(row.taskType) && data.aliId)
      ) {
        this.openVideo({
          aliId: data.aliId || "",
          filePath: data.filePath || (/\.mp4/i.test(String(text)) ? text : ""),
        });
        return;
      }
      const conf = this.getFileViewConf(row.taskType, key);
      const path =
        (conf && data[conf.urlName]) ||
        data.filePath ||
        data.declarationPath ||
        data.activityPlanPath ||
        data.letterOfCommitmentPath ||
        text;
      if (!path || path === "-") return;
      this.openFilePreview(path, row.name || "文件预览");
    },
    openFilePreview(path, name) {
      if (!path) return;
      const s = String(path).toLowerCase();
      if (s.includes(".mp4")) {
        this.openVideo({ filePath: path, aliId: "" });
        return;
      }
      this.openPdf(path, name);
    },
    openPdf(path, name) {
      if (!path) return;
      this.previewTitle = name || "文件预览";
      this.previewUrl = path;
      this.showPdf = true;
    },
    openVideo(row) {
      if (!row) return;
      if (!row.aliId && !row.filePath) {
        this.$message.warning("暂无可预览的视频");
        return;
      }
      this.videoData = {
        aliId: row.aliId || "",
        filePath: row.filePath || "",
      };
      this.showVideo = true;
    },
    openLink(url) {
      if (!url) return;
      const href = /^https?:\/\//i.test(url) ? url : `http://${url}`;
      window.open(href, "_blank");
    },
    openCompare(item, moduleTitle) {
      this.compareItem = item;
      this.compareModuleTitle = moduleTitle || "";
      this.compareVisible = true;
    },
    closeCompare() {
      this.compareVisible = false;
      this.compareItem = null;
      this.compareModuleTitle = "";
      this.showPdf = false;
      this.showVideo = false;
      this.previewUrl = "";
      this.videoData = {};
    },
    load() {
      this.loading = true;
      this.rawMap = {};
      // 变更历史：预取版本列表，用于卡片「查看详情」定位 versionId
      if (this.mode === "history") {
        this.versionIdMap = {};
        getVersionHistory(this.applyId)
          .then((res) => {
            if (res.code === 200 || res.code === 0) {
              const list = res.result || [];
              const map = {};
              list.forEach((v) => {
                if (v && v.version != null) map[v.version] = v.id;
              });
              this.versionIdMap = map;
            }
          })
          .catch(() => {});
      }
      const api =
        this.mode === "history"
          ? getModuleAuditList
          : getModulePendingAuditList;
      api({ applyId: this.applyId })
        .then((res) => {
          if (res.code === 200 || res.code === 0) {
            const raw = res.result || {};
            if (this.mode === "requests") {
              Object.keys(raw).forEach((key) => {
                raw[key] = (raw[key] || []).filter((i) => {
                  // 已驳回(3)在「变更历史」展示，不在「变更请求」展示
                  if (String(i.auditState) === "3") return false;
                  return this.isAuditorRole ? String(i.auditState) !== "0" : true;
                });
              });
            }
            this.rawMap = raw;
            let count = 0;
            Object.values(this.rawMap).forEach((arr) => {
              if (Array.isArray(arr)) count += arr.length;
            });
            // 变更历史：数量按时间轴卡片数统计（一个批次/版本算一张卡片）
            if (this.mode === "history") {
              count = this.historyTimeline.length;
            }
            this.$emit("loaded", { count, map: this.rawMap });
          } else {
            this.$message.error(res.message || "加载失败");
            this.$emit("loaded", { count: 0, map: {} });
          }
        })
        .catch(() => {
          this.$message.error("加载失败");
          this.$emit("loaded", { count: 0, map: {} });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    /** 打开版本详情弹窗：加载该版本快照并只读渲染 */
    openVersionDetail(entry) {
      if (!entry || !entry.versionId) return;
      this.detailItem = entry;
      this.detailTitle = `${entry.verText || "版本"}　${entry.title || ""}　${entry.time || ""}`;
      this.detailSnapshotState = null;
      this.detailLegacy = false;
      this.detailVisible = true;
      getVersionDetail(entry.versionId)
        .then((res) => {
          if (res.code === 200 || res.code === 0) {
            const data = res.result || {};
            this.detailLegacy = !!data.snapshotLegacy;
            this.detailSnapshotState = data.state || { base: {}, modules: {} };
          } else {
            this.$message.error(res.message || "加载版本详情失败");
          }
        })
        .catch(() => {
          this.$message.error("加载版本详情失败");
        });
    },
    closeDetail() {
      this.detailVisible = false;
      this.detailItem = null;
      this.detailTitle = "";
      this.detailSnapshotState = null;
      this.detailLegacy = false;
    },
    handleBatchAudit(batch, action) {
      if (!batch || !(batch.pendingIds || []).length) return;
      if (action === "reject") {
        this.rejectBatch = batch;
        this.rejectRemark = "";
        this.rejectVisible = true;
        return;
      }
      const count = batch.pendingIds.length;
      const isSchool = this.role === "school-role";
      this.$confirm({
        title: "确认整批通过",
        cancelText: "取消",
        okText: "确认",
        content: isSchool
          ? `将通过本批全部 ${count} 条变更，转教育局/厅审核。教育局/厅通过后才会写入项目并生成新版本。`
          : `将通过本批全部 ${count} 条变更。通过后会写入项目并生成新版本。`,
        onOk: () => this.executeBatchAudit(batch, "pass", ""),
      });
    },
    confirmBatchReject() {
      const batch = this.rejectBatch;
      if (!batch) {
        this.rejectVisible = false;
        return;
      }
      return this.executeBatchAudit(batch, "reject", this.rejectRemark).then(
        () => {
          this.rejectVisible = false;
          this.rejectBatch = null;
        }
      );
    },
    async executeBatchAudit(batch, action, remark) {
      const params = {
        applyId: this.applyId,
        auditState: action === "pass" ? "1" : "2",
        remark: remark || "",
        auditLevel: getAuditLevel(this.role),
      };
      if (batch.batchNumber) {
        params.batchNumber = batch.batchNumber;
      } else {
        return this.executeAudit(action, batch.pendingIds.join(","), remark);
      }
      try {
        const res = await auditByBatchNumber(params);
        if (res.code === 200) {
          this.$message.success(
            action === "pass" ? "已整批通过" : "已整批驳回"
          );
          this.load();
          this.$emit("audited");
        } else {
          this.$message.error(res.message || "操作失败");
          return Promise.reject(res);
        }
      } catch (e) {
        this.$message.error("审核操作失败");
        return Promise.reject(e);
      }
    },
    handleItemAudit(item, action) {
      const actionText = action === "pass" ? "通过" : "驳回";
      this.$confirm({
        title: `确认${actionText}审核`,
        cancelText: "取消",
        okText: "确认",
        content: `您确定要${actionText}该变更请求吗？`,
        onOk: () => {
          if (action === "reject") {
            this.$refs.rejectReason.init(item.id, "right");
            return;
          }
          return this.executeAudit("pass", item.id);
        },
      });
    },
    async executeAudit(action, taskIds, remark) {
      const params = {
        taskIds,
        auditState: action === "pass" ? 1 : 2,
        remark: remark || "",
        auditLevel: getAuditLevel(this.role),
      };
      try {
        const res = await postAction("/audit/lxAuditTask/auditTask", params);
        if (res.code === 200) {
          this.$message.success(
            `已成功${action === "pass" ? "通过" : "驳回"}审核`
          );
          this.load();
          this.$emit("audited");
        } else {
          this.$message.error(res.message || "操作失败");
        }
      } catch (e) {
        this.$message.error("审核操作失败");
      }
    },
    onRejectSuccess() {
      this.load();
      this.$emit("audited");
    },
    handleCancel(item, moduleTitle) {
      this.$confirm({
        title: "确认撤销申请",
        cancelText: "取消",
        okText: "确认",
        content: `您确定要撤销「${moduleTitle || ""}(${
          this.typeMap[item.operation] || item.operation
        })」吗？撤销后该条将从变更请求中移除，如需再改请重新发起更新。`,
        onOk: () => {
          return postAction(
            `/audit/lxAuditTask/cancelTask?ids=${item.id}`
          ).then((res) => {
            if (res.code == 200) {
              this.$message.success("撤销成功");
              this.load();
              this.$emit("audited");
            } else {
              this.$message.error(res.message || "撤销失败");
            }
          });
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ps-change-list {
  padding: 4px 0 8px;
  background: transparent;
  border-radius: 0;
  min-height: 240px;
  &__note {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fefce8;
    border: 1px solid #fde047;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 13px;
    color: #854d0e;
    margin-bottom: 14px;
  }
  &__empty {
    text-align: center;
    padding: 48px 20px;
    color: #9ca3af;
    .big {
      font-size: 34px;
      margin-bottom: 8px;
    }
  }
}
.ps-batch {
  margin-bottom: 22px;
  &__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 12px;
  }
  &__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    font-size: 13px;
    color: #6b7280;
  }
  &__title {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
  }
  &__actions {
    display: flex;
    gap: 8px;
  }
}
/* ===== 变更历史时间轴（对齐原型） ===== */
.ps-timeline {
  position: relative;
  padding-left: 28px;
  border-left: 2px solid #e5e7eb;
}
.tl-item {
  position: relative;
  margin-bottom: 24px;
}
.tl-dot {
  position: absolute;
  left: -33px;
  top: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 4px solid #fff;
  box-shadow: 0 0 0 2px #d1d5db;
  &.orange {
    background: #f97316;
  }
  &.green {
    background: #22c55e;
  }
  &.blue {
    background: #3b82f6;
  }
  &.red {
    background: #ef4444;
  }
}
.tl-card {
  border-radius: 10px;
  padding: 14px;
  &.orange {
    background: #fff7ed;
    border: 1px solid #fed7aa;
  }
  &.green {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
  }
  &.blue {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
  }
  &.red {
    background: #fef2f2;
    border: 1px solid #fecaca;
  }
}
.tl-top {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  gap: 10px;
}
.tl-ver {
  font-weight: 700;
  &.orange {
    color: #c2410c;
  }
  &.green {
    color: #15803d;
  }
  &.blue {
    color: #1d4ed8;
  }
  &.red {
    color: #b91c1c;
  }
}
.tl-time {
  font-size: 12px;
  color: #6b7280;
  flex-shrink: 0;
}
.tl-msg {
  font-size: 13px;
  color: #374151;
  margin-bottom: 8px;
  line-height: 1.7;
}
.tl-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.tl-tag {
  display: inline-block;
  font-size: 11px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 1px 8px;
  color: #4b5563;
}
.tl-actions {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}
.ps-change-group {
  margin-bottom: 20px;
  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #2563eb;
    border-left: 4px solid #2563eb;
    padding-left: 12px;
    margin-bottom: 12px;
  }
  &__count {
    font-size: 12px;
    color: #9ca3af;
    font-weight: 400;
    margin-left: 8px;
  }
}
.ps-req-card {
  border: 1px solid #fca5a5;
  background: #fff;
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 12px;
  &.reviewed {
    border-color: #e5e7eb;
    background: #f9fafb;
  }
  &__top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    flex-wrap: wrap;
    gap: 8px;
  }
  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    font-weight: 700;
    font-size: 14px;
  }
  &__op {
    font-size: 12px;
    color: #1d4ed8;
    background: #dbeafe;
    padding: 2px 8px;
    border-radius: 4px;
  }
  &__desc {
    font-weight: 400;
    color: #374151;
    font-size: 13px;
  }
  &__time {
    font-size: 12px;
    color: #6b7280;
  }
  &__remark {
    font-size: 13px;
    color: #b91c1c;
    margin-bottom: 8px;
  }
  &__hint {
    font-size: 13px;
    color: #6b7280;
    margin: 8px 0;
  }
  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #f3f4f6;
    flex-wrap: wrap;
  }
}
.ps-req-fields {
  margin: 10px 0;
}
.ps-req-field {
  display: flex;
  gap: 10px;
  font-size: 12px;
  padding: 7px 10px;
  background: #f8fafc;
  border-radius: 6px;
  margin-bottom: 6px;
  align-items: flex-start;
  .fname {
    width: 110px;
    color: #6b7280;
    flex-shrink: 0;
  }
  .old {
    color: #b91c1c;
    text-decoration: line-through;
    flex: 1;
    word-break: break-all;
  }
  .new {
    color: #15803d;
    flex: 1;
    word-break: break-all;
  }
  .arrow {
    color: #9ca3af;
    flex-shrink: 0;
  }
}
.ps-req-field-more {
  font-size: 12px;
  color: #9ca3af;
  padding: 2px 10px;
}
.ps-act-btn {
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  &--compare {
    background: #fff;
    border-color: #2563eb;
    color: #2563eb;
    &:hover {
      background: #eff6ff;
    }
  }
  &--detail {
    background: #2563eb;
    color: #fff;
    &:hover {
      background: #1d4ed8;
    }
  }
  &--pass {
    background: #16a34a;
    color: #fff;
    &:hover {
      background: #15803d;
    }
  }
  &--reject {
    background: #fff;
    border-color: #ef4444;
    color: #ef4444;
    &:hover {
      background: #fef2f2;
    }
  }
  &--cancel {
    background: #fff;
    border-color: #d1d5db;
    color: #374151;
    &:hover {
      border-color: #2563eb;
      color: #2563eb;
    }
  }
}

/* 对比弹窗（与页面其他 ps-modal 一致） */
.ps-modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: none;
  align-items: center;
  justify-content: center;
  padding: 20px;
  &.open {
    display: flex;
  }
}
.ps-modal {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 1024px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  &--detail {
    max-width: 1200px;
  }
}
.ps-version__legacy {
  background: #fefce8;
  border: 1px solid #fde047;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: #854d0e;
  margin-bottom: 10px;
}
.ps-modal-head {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  h3 {
    font-size: 21px;
    margin: 0;
    color: #111827;
  }
  p {
    font-size: 15px;
    color: #6b7280;
    margin: 4px 0 0;
  }
  .c-red {
    color: #b91c1c;
  }
  .c-green {
    color: #15803d;
  }
}
.ps-close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
  &:hover {
    color: #111827;
  }
}
.ps-modal-body {
  flex: 1;
  overflow: auto;
  padding: 20px;
}
.ps-cmp-meta {
  font-size: 15px;
  color: #6b7280;
  margin-bottom: 14px;
  .dot {
    margin: 0 6px;
    color: #d1d5db;
  }
}
.ps-compare-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.ps-cmp-head {
  font-size: 17px;
  font-weight: 500;
  padding: 9px 12px;
  border-radius: 8px 8px 0 0;
  &.gray {
    background: #f3f4f6;
    color: #374151;
  }
  &.blue {
    background: #2563eb;
    color: #fff;
  }
}
.ps-cmp-body {
  border: 1px solid #e5e7eb;
  border-top: none;
  border-radius: 0 0 8px 8px;
  padding: 16px;
  font-size: 16px;
  min-height: 120px;
  &.blue-border {
    border-color: #2563eb;
  }
}
.ps-cmp-line {
  margin-bottom: 10px;
  word-break: break-all;
  .k {
    color: #6b7280;
  }
}
.ps-cmp-link {
  color: #e5914b;
  cursor: pointer;
  text-decoration: underline;
  &:hover {
    color: #c6783a;
  }
}
.ps-cmp-deleted {
  color: #b91c1c;
  font-size: 18px;
  font-weight: 600;
  padding: 24px 8px;
  text-align: center;
}
.diff-new {
  background: #dcfce7;
  padding: 0 4px;
  border-radius: 3px;
  color: #15803d;
}
.ps-change-alert {
  margin-top: 16px;
  background: #fefce8;
  border: 1px solid #fde047;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 15px;
  color: #854d0e;
}
.ps-cmp-empty {
  text-align: center;
  color: #9ca3af;
  padding: 40px 0;
}
.preview-pop-box {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 2100;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  .preview-boxs {
    width: 90%;
    max-width: 1100px;
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
  }
}
.ps-modal-foot {
  padding: 14px 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
